import React, { useEffect, useState } from "react"
import { Button, Card, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import AddEditFee from "./AddEditFee"
import SweetAlerts from "../../../components/SweetAlerts"
import AddEditConvenienceFee from "./AddEditConvenienceFee"
import FeeSkeleton from "../../../skeleton/settings/Fee"
import AddEditGST from "./AddEditGST"
import { useSelector } from "react-redux"

const FeeManagement = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [convenienceShow, setConvenienceShow] = useState(false)
  const [gstShow, setGstShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [isUpdateConvenienceFees, setIsUpdateConvenienceFees] = useState(false)
  const [isUpdateGST, setIsUpdateGST] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(true)
  const { userDetails } = useSelector((state) => state.basicInformation)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => setShow(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setIsDelete(false)
    setShowAlert(false)
    setConvenienceShow(false)
    setGstShow(false)
  }

  const fetchData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.feeList,
      })
      handleAccess({ code: response?.data?.code })
      const data = response?.data?.data
      data?.sort((value, nextValue) => nextValue.id - value.id)
      setData(data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error)
    }
  }

  const handleDelete = async (id) => {
    try {
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteFee + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) => data.filter((user) => user.id !== id))
        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response?.data?.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <>
      {canAdd && userDetails?.dataScope !== "OWN" ? (
        <PageTitleButton
          PagetitleText={"Fees Management"}
          HeaderButtonText={"Add Fee"}
          IconclassName={"plus_black"}
          onClick={handleShow}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Fees Management"}
          onClick={handleShow}
        />
      )}

      <div className="d-flex justify-content-end mb-3 gap-3">
        {canEdit && userDetails?.dataScope !== "OWN" && (
          <Button
            variant="primary"
            className="d-inline-flex gap-2 align-items-center"
            onClick={() => {
              setConvenienceShow(true)
              setIsUpdateConvenienceFees({
                convenience_fee: data?.[0]?.convenience_fee,
              })
            }}
          >
            <i
              className="icon_main edit_pencil bg-white"
              style={{ width: "1rem", height: "1rem" }}
            ></i>
            Convenience Fee
          </Button>
        )}

        {canEdit && userDetails?.dataScope !== "OWN" && (
          <Button
            variant="primary"
            className="d-inline-flex gap-2 align-items-center"
            onClick={() => {
              setGstShow(true)
              setIsUpdateGST({
                gst: data?.[0]?.gst,
              })
            }}
          >
            <i
              className="icon_main edit_pencil bg-white"
              style={{ width: "1rem", height: "1rem" }}
            ></i>
            GST
          </Button>
        )}
      </div>

      {!loading ? (
        <Card>
          <Card.Body>
            <Table className="rounded table-striped" responsive="md">
              <thead>
                <tr>
                  <th className="text-center">Station ID</th>
                  <th className="text-center">Station Name</th>
                  <th className="text-center">Base Fee(₹)</th>
                  <th className="text-center">Quick Booking Base Fee(₹)</th>
                  <th className="text-center">Parking Fee(₹)</th>
                  <th className="text-center">Convenience Fee(₹)</th>
                  <th className="text-center">GST(%)</th>
                  {(canEdit || canDelete) && (
                    <th className="text-center">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data &&
                  data?.length > 0 &&
                  data?.map((data, index) => (
                    <tr key={index + 1}>
                      <td className="text-center">{data?.station?.id}</td>
                      <td className="text-center">
                        {data?.station?.station_name}
                      </td>
                      <td className="text-center">{data.base_fee}</td>
                      <td className="text-center">
                        {data.instant_booking_base_fee}
                      </td>
                      <td className="text-center">{data.parking_fee}</td>
                      <td className="text-center">{data.convenience_fee}</td>
                      <td className="text-center">{data.gst}</td>

                      {(canEdit || canDelete) && (
                        <td className="text-center">
                          <div className="d-inline-flex gap-2">
                            {canEdit && (
                              <TooltipComponent
                                toolplace="top"
                                tooltipText="Edit"
                              >
                                <Button
                                  variant="btnedit_icon"
                                  className="btn_icon"
                                  onClick={() => {
                                    handleShow()
                                    setIsUpdate(data)
                                  }}
                                >
                                  <i className="icon_main edit_pencil"></i>
                                </Button>
                              </TooltipComponent>
                            )}
                            {canDelete && userDetails?.dataScope !== "OWN" && (
                              <TooltipComponent
                                toolplace="top"
                                tooltipText="Delete"
                              >
                                <Button
                                  variant="btndelete_icon"
                                  className="btn_icon"
                                  onClick={() => {
                                    setShowAlert(true)
                                    setIsDelete(data)
                                  }}
                                >
                                  <i className="icon_main delete_trash"></i>
                                </Button>
                              </TooltipComponent>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                {(data?.length === 0 || data?.length === undefined) && (
                  <tr>
                    <td className="text-center py-4" colSpan={6}>
                      <div className="norecord_wrapper">
                        <img src={recordNotFound} alt="No Record Found" />
                        <p className="norecords_title">No Records Found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <FeeSkeleton />
      )}

      <AddEditFee
        show={show}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
      />
      <AddEditConvenienceFee
        show={convenienceShow}
        setShow={setConvenienceShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdateConvenienceFees={setIsUpdateConvenienceFees}
        isUpdateConvenienceFees={isUpdateConvenienceFees}
        setShowAlert={setShowAlert}
      />

      <AddEditGST
        show={gstShow}
        handleClose={handleClose}
        isUpdateGST={isUpdateGST}
        fetchData={fetchData}
        setIsUpdateGST={setIsUpdateGST}
        userRole={userDetails?.role}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={"Are you sure you want to Delete this Station Fee?"}
        handleClose={handleClose}
        iconClassName={"delete_trash"}
        handleAlert={() => {
          handleDelete(isDelete.id)
        }}
      />
    </>
  )
}

export default FeeManagement
